import React, { useState, useEffect } from 'react';
import { useRegion, getRegionLabel } from '../context/RegionContext';
import { useAuth } from '../context/AuthContext';
import { getRegionStats } from '../services/api';
import {
  Globe2, MapPin, Briefcase, Users, Video, ArrowRight, Loader2, Lock
} from 'lucide-react';

const REGIONS = [
  {
    key: 'NA',
    label: 'North America',
    subtitle: 'United States, Canada, Mexico',
    gradient: 'from-blue-600 to-cyan-500',
    iconBg: 'bg-blue-500/20',
    iconColor: 'text-blue-400',
    borderHover: 'hover:border-blue-500/50',
    shadowHover: 'hover:shadow-blue-500/10',
  },
  {
    key: 'UK_EMEA',
    label: 'UK & EMEA',
    subtitle: 'United Kingdom, Europe, Middle East, Africa',
    gradient: 'from-emerald-600 to-teal-500',
    iconBg: 'bg-emerald-500/20',
    iconColor: 'text-emerald-400',
    borderHover: 'hover:border-emerald-500/50',
    shadowHover: 'hover:shadow-emerald-500/10',
  },
  {
    key: 'APAC',
    label: 'APAC',
    subtitle: 'Asia Pacific, India, Australia',
    gradient: 'from-violet-600 to-purple-500',
    iconBg: 'bg-violet-500/20',
    iconColor: 'text-violet-400',
    borderHover: 'hover:border-violet-500/50',
    shadowHover: 'hover:shadow-violet-500/10',
  },
];

export const RegionSelector = () => {
  const { setRegion } = useRegion();
  const { user } = useAuth();
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [hoveredRegion, setHoveredRegion] = useState(null);

  // Determine which regions this user can access
  const userRegions = user?.role === 'admin' ? ['NA', 'UK_EMEA', 'APAC'] : (user?.regions || []);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await getRegionStats();
        setStats(data);
      } catch (e) {
        console.error('Failed to load region stats:', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
        fontFamily: 'Manrope, system-ui, sans-serif',
        padding: '40px 20px',
      }}
      data-testid="region-selector"
    >
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: 48 }}>
        <div style={{
          width: 64, height: 64, borderRadius: 18,
          background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 20px',
          boxShadow: '0 0 40px rgba(99, 102, 241, 0.3)',
        }}>
          <Globe2 size={32} color="#fff" strokeWidth={1.5} />
        </div>
        <h1 style={{ color: '#f1f5f9', fontSize: 28, fontWeight: 800, margin: '0 0 8px', letterSpacing: '-0.02em' }}>
          Select Your Region
        </h1>
        <p style={{ color: '#94a3b8', fontSize: 15, margin: 0, maxWidth: 420 }}>
          Choose a region to access its recruitment pipeline. You can switch regions anytime from the sidebar.
        </p>
      </div>

      {/* Region Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: 24,
        maxWidth: 960,
        width: '100%',
      }}>
        {REGIONS.map((r) => {
          const regionStats = stats[r.key] || { jobs: 0, candidates: 0, interviews: 0 };
          const isHovered = hoveredRegion === r.key;
          const isAllowed = userRegions.includes(r.key);
          const isRestricted = regionStats.restricted;

          return (
            <button
              key={r.key}
              onClick={() => isAllowed && setRegion(r.key)}
              onMouseEnter={() => setHoveredRegion(r.key)}
              onMouseLeave={() => setHoveredRegion(null)}
              data-testid={`region-card-${r.key}`}
              disabled={!isAllowed}
              style={{
                background: '#1e293b',
                border: `1.5px solid ${!isAllowed ? '#1e293b' : isHovered ? 'rgba(99, 102, 241, 0.5)' : '#334155'}`,
                borderRadius: 20,
                padding: 0,
                cursor: 'pointer',
                textAlign: 'left',
                transition: 'all 0.3s ease',
                transform: isHovered ? 'translateY(-4px)' : 'none',
                boxShadow: isHovered
                  ? '0 20px 40px rgba(0,0,0,0.3), 0 0 60px rgba(99, 102, 241, 0.1)'
                  : '0 4px 20px rgba(0,0,0,0.2)',
                overflow: 'hidden',
              }}
            >
              {/* Gradient top bar */}
              <div style={{
                height: 4,
                background: `linear-gradient(to right, var(--tw-gradient-from), var(--tw-gradient-to))`,
                backgroundImage: r.key === 'NA'
                  ? 'linear-gradient(to right, #2563eb, #06b6d4)'
                  : r.key === 'UK_EMEA'
                  ? 'linear-gradient(to right, #059669, #14b8a6)'
                  : 'linear-gradient(to right, #7c3aed, #a855f7)',
              }} />

              <div style={{ padding: '28px 24px 24px' }}>
                {/* Icon + Label */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 6 }}>
                  <div style={{
                    width: 44, height: 44, borderRadius: 12,
                    background: r.key === 'NA' ? 'rgba(37, 99, 235, 0.15)' : r.key === 'UK_EMEA' ? 'rgba(5, 150, 105, 0.15)' : 'rgba(124, 58, 237, 0.15)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <MapPin size={22} color={r.key === 'NA' ? '#60a5fa' : r.key === 'UK_EMEA' ? '#34d399' : '#a78bfa'} strokeWidth={1.8} />
                  </div>
                  <div>
                    <h2 style={{ color: '#f1f5f9', fontSize: 20, fontWeight: 700, margin: 0 }}>{r.label}</h2>
                    <p style={{ color: '#64748b', fontSize: 12, margin: 0 }}>{r.subtitle}</p>
                  </div>
                </div>

                {/* Stats */}
                {loading ? (
                  <div style={{ display: 'flex', justifyContent: 'center', padding: '20px 0' }}>
                    <Loader2 size={20} color="#64748b" style={{ animation: 'spin 1s linear infinite' }} />
                  </div>
                ) : (
                  <div style={{
                    display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12,
                    marginTop: 20, padding: '14px 0',
                    borderTop: '1px solid #334155',
                  }}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, marginBottom: 4 }}>
                        <Briefcase size={13} color="#94a3b8" />
                        <span style={{ color: '#f1f5f9', fontSize: 22, fontWeight: 700, fontFamily: 'JetBrains Mono, monospace' }}>
                          {regionStats.jobs}
                        </span>
                      </div>
                      <span style={{ color: '#64748b', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Jobs</span>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, marginBottom: 4 }}>
                        <Users size={13} color="#94a3b8" />
                        <span style={{ color: '#f1f5f9', fontSize: 22, fontWeight: 700, fontFamily: 'JetBrains Mono, monospace' }}>
                          {regionStats.candidates}
                        </span>
                      </div>
                      <span style={{ color: '#64748b', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Candidates</span>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, marginBottom: 4 }}>
                        <Video size={13} color="#94a3b8" />
                        <span style={{ color: '#f1f5f9', fontSize: 22, fontWeight: 700, fontFamily: 'JetBrains Mono, monospace' }}>
                          {regionStats.interviews}
                        </span>
                      </div>
                      <span style={{ color: '#64748b', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Interviews</span>
                    </div>
                  </div>
                )}

                {/* Enter button */}
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  marginTop: 16, padding: '10px 0',
                  borderRadius: 10,
                  background: !isAllowed ? 'rgba(239, 68, 68, 0.08)'
                    : isHovered
                    ? (r.key === 'NA' ? 'rgba(37, 99, 235, 0.15)' : r.key === 'UK_EMEA' ? 'rgba(5, 150, 105, 0.15)' : 'rgba(124, 58, 237, 0.15)')
                    : 'rgba(51, 65, 85, 0.3)',
                  transition: 'all 0.3s ease',
                  opacity: isAllowed ? 1 : 0.5,
                }}>
                  {isAllowed ? (
                    <>
                      <span style={{
                        color: isHovered ? '#f1f5f9' : '#94a3b8',
                        fontSize: 14, fontWeight: 600, transition: 'color 0.3s',
                      }}>
                        Enter {r.label}
                      </span>
                      <ArrowRight size={16} color={isHovered ? '#f1f5f9' : '#94a3b8'} style={{
                        transition: 'transform 0.3s',
                        transform: isHovered ? 'translateX(4px)' : 'none',
                      }} />
                    </>
                  ) : (
                    <>
                      <Lock size={14} color="#ef4444" />
                      <span style={{ color: '#ef4444', fontSize: 13, fontWeight: 600 }}>No Access</span>
                    </>
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Footer hint */}
      <p style={{ color: '#475569', fontSize: 13, marginTop: 32 }}>
        HireFast AI  &middot;  Enterprise Recruitment Intelligence
      </p>
    </div>
  );
};

export default RegionSelector;
